import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  CountryISO,
  PaymentInitiationLinkTokenRequestDto,
  PaymentInitiationLinkTokenResponseDto,
} from "@ifgengineering/client-invest-sdk";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { logException } from "../../helpers/logException/logException";
import { getUserMessage } from "@helpers/apiErrors";
import { fetchBankAccountsAPI } from "../bankAccount/bankAccount.actions";
import {
  PlaidCoverageResponse,
  PlaidLinkTokenResponse,
  CreateBankAccountFromPlaidRequest,
  CreateBankAccountFromPlaidResponse,
} from "./plaid.types";

const CHECK_PLAID_COVERAGE_API = "CHECK_PLAID_COVERAGE_API";
const GET_PLAID_LINK_TOKEN_API = "GET_PLAID_LINK_TOKEN_API";
const GET_PAYMENT_INITIATION_LINK_TOKEN_API =
  "GET_PAYMENT_INITIATION_LINK_TOKEN_API";
const GET_IDV_LINK_TOKEN_API = "GET_IDV_LINK_TOKEN_API";
const CREATE_BANK_ACCOUNT_FROM_PLAID_API = "CREATE_BANK_ACCOUNT_FROM_PLAID_API";
const SET_SELECTED_COUNTRY = "SET_SELECTED_COUNTRY";
const RESET_PLAID_STATE = "RESET_PLAID_STATE";
const RESET_VERIFICATION_STATUS = "RESET_VERIFICATION_STATUS";
const CLEAR_PAYMENT_INITIATION_LINK_TOKEN =
  "CLEAR_PAYMENT_INITIATION_LINK_TOKEN";

export const setSelectedCountry =
  createAction<CountryISO>(SET_SELECTED_COUNTRY);

export const resetPlaidState = createAction(RESET_PLAID_STATE);

export const resetVerificationStatus = createAction(RESET_VERIFICATION_STATUS);

export const clearPaymentInitiationLinkToken = createAction(
  CLEAR_PAYMENT_INITIATION_LINK_TOKEN
);

const CLEAR_IDV_LINK_TOKEN = "CLEAR_IDV_LINK_TOKEN";

export const clearIdvLinkToken = createAction(CLEAR_IDV_LINK_TOKEN);

export const checkPlaidCoverageAPI = createAsyncThunk<
  PlaidCoverageResponse,
  CountryISO
>(CHECK_PLAID_COVERAGE_API, async (country, { rejectWithValue }) => {
  try {
    const response = await clientInvestSdk.checkPlaidCoverage(country);
    return response.data;
  } catch (e: any) {
    logException({
      tag: "checkPlaidCoverageAPI",
      exception: e,
    });

    return rejectWithValue("Failed to check Plaid coverage");
  }
});

export const getPlaidLinkTokenAPI = createAsyncThunk<
  PlaidLinkTokenResponse,
  CountryISO
>(GET_PLAID_LINK_TOKEN_API, async (_country, { rejectWithValue }) => {
  try {
    // Note: country parameter is not currently used by the API
    // The backend determines coverage based on configuration
    const response = await clientInvestSdk.getBankVerificationLinkToken();
    return response.data;
  } catch (e: any) {
    logException({
      tag: "getPlaidLinkTokenAPI",
      exception: e,
    });

    console.error("Failed to get Plaid link token:", e);
    toast.error("Failed to get Plaid link token. Please try again.");
    return rejectWithValue("Failed to get Plaid link token");
  }
});

export const getPaymentInitiationLinkTokenAPI = createAsyncThunk<
  PaymentInitiationLinkTokenResponseDto,
  PaymentInitiationLinkTokenRequestDto
>(GET_PAYMENT_INITIATION_LINK_TOKEN_API, async (data, { rejectWithValue }) => {
  try {
    const response = await clientInvestSdk.getPaymentInitiationLinkToken(data);
    return response.data;
  } catch (e: any) {
    logException({
      tag: "getPaymentInitiationLinkTokenAPI",
      exception: e,
    });

    toast.error(
      "Failed to initiate instant bank transfer. Please try again or use manual transfer."
    );
    return rejectWithValue("Failed to get payment initiation link token");
  }
});

export const getIdvLinkTokenAPI = createAsyncThunk<PlaidLinkTokenResponse>(
  GET_IDV_LINK_TOKEN_API,
  async (_, { rejectWithValue }) => {
    try {
      const response = await clientInvestSdk.getIdvLinkToken();
      return response.data;
    } catch (e: unknown) {
      logException({
        tag: "getIdvLinkTokenAPI",
        exception: e,
      });

      toast.error(
        getUserMessage(
          e,
          "Failed to start identity verification. Please try again."
        )
      );
      return rejectWithValue("Failed to get Plaid IDV link token");
    }
  }
);

export const createBankAccountFromPlaidAPI = createAsyncThunk<
  CreateBankAccountFromPlaidResponse,
  CreateBankAccountFromPlaidRequest,
  { rejectValue: string }
>(
  CREATE_BANK_ACCOUNT_FROM_PLAID_API,
  async (data, { rejectWithValue, dispatch }) => {
    try {
      const response = await clientInvestSdk.createBankAccountFromPlaid(data);

      toast.success("Bank account added successfully");

      dispatch(fetchBankAccountsAPI());

      return response.data;
    } catch (e: unknown) {
      logException({
        tag: "createBankAccountFromPlaidAPI",
        exception: e,
      });

      const error = e as { response?: { data?: { message?: string } } };
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to add bank account. Please try again.";

      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
